/**
 * Aggregate Payment Status Utility
 * Computes paid vs. pending totals from reservations_minimal array
 * 
 * Uses canonical_financial_summary.owner_payout.payout_status to determine status
 */

export function aggregatePaymentStatus(reservations = []) {
  let paidTotal = 0;
  let pendingTotal = 0;
  let estimatedTotal = 0;
  
  const paidReservations = [];
  const pendingReservations = [];
  const estimatedReservations = [];
  
  let nextPayoutDate = null;
  let nextPayoutAmount = 0;
  
  reservations.forEach(reservation => {
    const canonical = reservation.canonical_financial_summary || {};
    const ownerPayout = canonical.owner_payout || {};
    const payoutTotal = ownerPayout.owner_payout_total || 0;
    const payoutStatus = ownerPayout.payout_status || 'pending';
    const estimatedDate = ownerPayout.estimated_payout_date || null;
    
    // Determine status (paid, pending, estimated)
    // Status values: 'paid', 'pending', 'estimated', 'completed', etc.
    const status = payoutStatus.toLowerCase();
    
    const reservationData = {
      reservation_id: reservation.reservation_id,
      guest_name: reservation.guest_name || null,
      checkin_date: reservation.checkin_date,
      checkout_date: reservation.checkout_date,
      payout_total: payoutTotal,
      payout_status: payoutStatus,
      estimated_payout_date: estimatedDate,
    };
    
    if (status === 'paid' || status === 'completed' || status === 'deposited') {
      paidTotal += payoutTotal;
      paidReservations.push(reservationData);
    } else if (status === 'estimated' || status === 'provisional') {
      estimatedTotal += payoutTotal;
      estimatedReservations.push(reservationData);
      
      // Track earliest estimated date for next payout
      if (estimatedDate && (!nextPayoutDate || new Date(estimatedDate) < new Date(nextPayoutDate))) {
        nextPayoutDate = estimatedDate;
        nextPayoutAmount = payoutTotal;
      }
    } else {
      // Default to pending
      pendingTotal += payoutTotal;
      pendingReservations.push(reservationData);
      
      // Track earliest pending date for next payout
      if (estimatedDate && (!nextPayoutDate || new Date(estimatedDate) < new Date(nextPayoutDate))) {
        nextPayoutDate = estimatedDate;
        nextPayoutAmount = payoutTotal;
      }
    }
  });
  
  // If we have pending reservations, sum their amounts for next payout
  if (pendingReservations.length > 0 && !nextPayoutDate) {
    // Fallback: use earliest checkout date if no estimated date
    const dates = pendingReservations
      .map(r => r.checkout_date)
      .filter(Boolean)
      .sort();
    if (dates.length > 0) {
      nextPayoutDate = dates[0];
    }
    nextPayoutAmount = pendingTotal;
  }
  
  return {
    paidTotal,
    pendingTotal,
    estimatedTotal,
    paidReservations,
    pendingReservations,
    estimatedReservations,
    nextPayoutDate,
    nextPayoutAmount,
  };
}
